import React from 'react'
import { Navigate } from 'react-router-dom'
import { Loading, Message } from '@workwhile/ui'
import { parseErrorMessage } from 'api'
import { createContext } from 'lib/createContext'
import { useAssessmentDetail } from 'queries/admin/coach/useAssessmentDetail'
import { useSubmitAssessmentEvaluationsMutation } from 'queries/admin/coach/useSubmitAssessmentEvaluationsMutation'

type AssessmentDetailContextState = {
  state: NonNullable<ReturnType<typeof useAssessmentDetail>['data']> & {
    dataUpdatedAt: number
    isFetching: boolean
    isError: boolean
    error: string
    submitEvaluationsState: {
      isPending: boolean
      isSuccess: boolean
      isError: boolean
      errorMessage: string | undefined
    }
  }
  actions: {
    refetch: ReturnType<typeof useAssessmentDetail>['refetch']
    submitEvaluations: ReturnType<
      typeof useSubmitAssessmentEvaluationsMutation
    >['mutate']
    reset: ReturnType<typeof useSubmitAssessmentEvaluationsMutation>['reset']
  }
}

const [AssessmentDetailContext, useAssessmentDetailContextValue] =
  createContext<AssessmentDetailContextState>({
    name: 'AssessmentDetailContext',
  })

export const AssessmentDetailProvider = ({
  children,
  positionId,
  userId,
}: {
  children: React.ReactNode
  positionId: number
  userId: number
}) => {
  const {
    data,
    isPending,
    isError,
    error,
    dataUpdatedAt,
    refetch,
    isFetching,
  } = useAssessmentDetail({ positionId, userId })

  const {
    mutate: submitEvaluations,
    reset,
    isPending: isSubmitPending,
    isSuccess: isSubmitSuccess,
    isError: isSubmitError,
    error: submitError,
  } = useSubmitAssessmentEvaluationsMutation()

  if (isPending) {
    return <Loading />
  }

  if (isError && !data) {
    console.error(error)
    return (
      <Message variant="error" m={4} borderRadius="small">
        Sorry, something went wrong. Please reach out to eng support with the
        URL of the page if this persists.
      </Message>
    )
  }

  if (!data) {
    return null
  }

  if (data.assessmentCalls.length === 0) {
    return <Navigate to="/not-found" />
  }

  const assessmentCalls = [...data.assessmentCalls].sort((a, b) =>
    a.endedAt.localeCompare(b.endedAt)
  )

  const value: AssessmentDetailContextState = {
    state: {
      ...data,
      assessmentCalls,
      dataUpdatedAt,
      isFetching,
      isError,
      error: parseErrorMessage(error),
      submitEvaluationsState: {
        isPending: isSubmitPending,
        isSuccess: isSubmitSuccess,
        isError: isSubmitError,
        errorMessage: parseErrorMessage(submitError),
      },
    },
    actions: {
      refetch,
      submitEvaluations,
      reset,
    },
  }

  return (
    <AssessmentDetailContext.Provider value={value}>
      {children}
    </AssessmentDetailContext.Provider>
  )
}

export { useAssessmentDetailContextValue }
